import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';
import { useId } from 'react';
import './ui.css';

/**
 * THE shared component layer (§0.5): "ONE shared components layer used by every
 * screen — no per-screen one-off styles."
 *
 * Every colour, radius and tap size comes from tokens.css. Screens compose these
 * and add layout only. Extended in later phases; never bypassed.
 */

export function Screen({ children, width = 'narrow' }: { children: ReactNode; width?: 'narrow' | 'wide' }) {
  return <main className={`screen screen--${width}`}>{children}</main>;
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <section className={`card ${className}`}>{children}</section>;
}

export function Title({ children, sub }: { children: ReactNode; sub?: ReactNode }) {
  return (
    <header className="titleblock">
      <h1 className="script titleblock__h">{children}</h1>
      {sub ? <p className="titleblock__sub">{sub}</p> : null}
    </header>
  );
}

export function Heading({ children, level = 2 }: { children: ReactNode; level?: 2 | 3 }) {
  const Tag = level === 2 ? 'h2' : 'h3';
  return <Tag className={`heading heading--${level}`}>{children}</Tag>;
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  /** Shows a spinner and disables the button. Every write disables while in flight (§0.5). */
  busy?: boolean;
  full?: boolean;
};

export function Button({
  variant = 'primary',
  busy = false,
  full = false,
  disabled,
  children,
  className = '',
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      className={`btn btn--${variant} ${full ? 'btn--full' : ''} ${className}`}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
    >
      {busy ? <span className="btn__spinner" aria-hidden="true" /> : null}
      <span>{children}</span>
    </button>
  );
}

type FieldProps = InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string; error?: string };

export function TextField({ label, hint, error, ...rest }: FieldProps) {
  const id = useId();
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <input
        {...rest}
        id={id}
        className={`field__input ${error ? 'field__input--bad' : ''}`}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-err` : hint ? `${id}-hint` : undefined}
      />
      {hint && !error ? (
        <p className="field__hint" id={`${id}-hint`}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p className="field__error" id={`${id}-err`}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Loading, error and empty are designed states, never blank screens (§0.5). */
export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="state" role="status">
      <span className="state__spinner" aria-hidden="true" />
      <span className="state__text">{label}</span>
    </div>
  );
}

export function ErrorState({
  message,
  onRetry,
  busy = false,
}: {
  message: string;
  onRetry?: () => void;
  busy?: boolean;
}) {
  return (
    <div className="state state--error" role="alert">
      <p className="state__error-line">{message}</p>
      {onRetry ? (
        <Button variant="secondary" onClick={onRetry} busy={busy}>
          Retry
        </Button>
      ) : null}
    </div>
  );
}

export function EmptyState({ line, action }: { line: string; action?: ReactNode }) {
  return (
    <div className="state state--empty">
      <p className="state__text">{line}</p>
      {action}
    </div>
  );
}

export function Chip({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'live' | 'urgent' | 'info' | 'gold';
}) {
  return <span className={`chip chip--${tone}`}>{children}</span>;
}

export function Row({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`row ${className}`}>{children}</div>;
}
